import React from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

export default function LegacyFindDialog({
  open,
  categories = [],
  categoryOptions = [],
  error = "",
  hasRecoverableError = false,
  isSubmitting = false,
  onClose,
  onRun,
  onShowNearbyNow,
  onToggleCategory,
}) {
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (isSubmitting) return;
        onClose?.();
      }}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Uncover Nearby Time Capsules</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
          Choose what you want to uncover.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          {categoryOptions.map((option) => {
            const selected = categories.includes(option.key);
            const IconComponent = option.icon;
            return (
              <Button
                key={option.key}
                onClick={() => onToggleCategory?.(option.key)}
                variant="outlined"
                startIcon={<IconComponent fontSize="small" />}
                sx={(theme) => ({
                  textTransform: "none",
                  justifyContent: "flex-start",
                  borderRadius: 2,
                  py: 1.2,
                  borderColor: selected ? theme.palette.success.main : theme.palette.divider,
                  color: selected ? theme.palette.success.main : theme.palette.text.primary,
                  backgroundColor: selected
                    ? theme.palette.mode === "dark"
                      ? alpha(theme.palette.success.main, 0.15)
                      : alpha(theme.palette.success.main, 0.08)
                    : "transparent",
                  "&:hover": {
                    borderColor: selected ? theme.palette.success.main : theme.palette.primary.main,
                    backgroundColor: selected
                      ? theme.palette.mode === "dark"
                        ? alpha(theme.palette.success.main, 0.2)
                        : alpha(theme.palette.success.main, 0.12)
                      : theme.palette.action.hover,
                  },
                })}
              >
                {option.label}
              </Button>
            );
          })}
        </Box>
        {error ? (
          <Box
            sx={(theme) => ({
              mt: 1.5,
              px: 1.25,
              py: 1,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.error.main, 0.18)
                  : alpha(theme.palette.error.main, 0.08),
              border: `1px solid ${alpha(theme.palette.error.main, 0.32)}`,
            })}
          >
            <ErrorOutlineIcon sx={{ fontSize: 18, mt: "2px", color: "error.main" }} />
            <Typography variant="caption" color="error" sx={{ display: "block", lineHeight: 1.4 }}>
              {error}
            </Typography>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ textTransform: "none", borderRadius: "999px" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onRun}
          disabled={isSubmitting || categories.length === 0}
          sx={(theme) => ({
            textTransform: "none",
            borderRadius: "999px",
            backgroundColor: theme.colors.green,
            color: theme.colors.white,
            "&:hover": {
              backgroundColor: theme.colors.green,
            },
          })}
        >
          {hasRecoverableError ? "Try uncover again" : "Uncover now"}
        </Button>
        {hasRecoverableError ? (
          <Button
            variant="text"
            onClick={onShowNearbyNow}
            disabled={isSubmitting}
            sx={{ gridColumn: "1 / -1", textTransform: "none", fontWeight: 700 }}
          >
            Show nearby now
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
